import React, { useState, useEffect } from 'react';
import { Plus, Sparkles, FileText, Loader2, CheckCircle, AlertCircle, Play, Trash2, Clock, X, Globe, ChevronDown, Crown, Lock } from 'lucide-react';
import { supabase, getLessonsTable, getProgressTable } from '../utils/supabase';
import { useTranslation } from '../utils/translations';

const TRANSLATION_LANGUAGES = [
  'English',
  'Persian',
  'Arabic',
  'Turkish',
  'Spanish',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Russian',
  'Chinese',
  'Japanese',
  'Korean',
  'Hindi',
  'Indonesian',
  'Vietnamese',
  'Thai',
  'Polish',
  'Ukrainian',
  'Romanian',
];

const FREE_LESSON_LIMIT = 3;

interface CustomLessonsListProps {
  userId: string;
  nativeLanguage?: string;
  isPremium?: boolean;
  onSelectLesson: (lessonId: string) => void;
  onBack: () => void;
}

interface CustomLesson {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
  created_at: string;
  completed?: boolean;
}

interface GenerationJob {
  id: string;
  status: string;
  input_type: string;
  input_text: string;
  progress: number;
  error_message: string | null;
  lesson_id: string | null;
  generated_title: string | null;
  created_at: string;
}

export function CustomLessonsList({ userId, nativeLanguage, isPremium, onSelectLesson, onBack }: CustomLessonsListProps) {
  const [lessons, setLessons] = useState<CustomLesson[]>([]);
  const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const { t, isRTL } = useTranslation(nativeLanguage);
  const prevJobIdsRef = React.useRef<Set<string>>(new Set());

  useEffect(() => {
    loadData();
    const interval = setInterval(pollJobs, 5000);
    return () => clearInterval(interval);
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    const [, newJobs] = await Promise.all([loadLessons(), loadPendingJobsRaw()]);
    if (newJobs) {
      prevJobIdsRef.current = new Set(newJobs.map((j: GenerationJob) => j.id));
      setJobs(newJobs);
    }
    setLoading(false);
  };

  const pollJobs = async () => {
    const newJobs = await loadPendingJobsRaw();
    if (!newJobs) return;

    const newJobIds = new Set(newJobs.map((j: GenerationJob) => j.id));
    const hadJobsThatFinished = [...prevJobIdsRef.current].some(id => !newJobIds.has(id));

    prevJobIdsRef.current = newJobIds;
    setJobs(newJobs);

    if (hadJobsThatFinished) {
      await loadLessons();
    }
  };

  const loadLessons = async () => {
    const lessonsTable = getLessonsTable(nativeLanguage);
    const progressTable = getProgressTable(nativeLanguage);

    const { data: lessonsData } = await supabase
      .from(lessonsTable)
      .select('id, title, description, category, difficulty, created_at')
      .eq('level', 'Custom')
      .eq('generated_for', userId)
      .order('created_at', { ascending: false });

    if (lessonsData) {
      const { data: progressData } = await supabase
        .from(progressTable)
        .select('lesson_id, completed')
        .eq('user_id', userId)
        .in('lesson_id', lessonsData.map(l => l.id));

      const progressMap = new Map(progressData?.map(p => [p.lesson_id, p.completed]) || []);

      setLessons(lessonsData.map(lesson => ({
        ...lesson,
        completed: progressMap.get(lesson.id) || false
      })));
    }
  };

  const loadPendingJobsRaw = async (): Promise<GenerationJob[] | null> => {
    const { data: jobsData } = await supabase
      .from('user_lesson_generation_jobs')
      .select('*')
      .eq('user_id', userId)
      .in('status', ['pending', 'generating_text', 'generating_audio', 'analyzing'])
      .order('created_at', { ascending: false });

    return jobsData || null;
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!confirm(t('Are you sure you want to delete this lesson?'))) return;

    const lessonsTable = getLessonsTable(nativeLanguage);
    await supabase.from(lessonsTable).delete().eq('id', lessonId);
    setLessons(prev => prev.filter(l => l.id !== lessonId));
  };

  const handleCancelJob = async (jobId: string) => {
    if (!confirm(t('Cancel this lesson generation?'))) return;

    await supabase
      .from('user_lesson_generation_jobs')
      .delete()
      .eq('id', jobId);
    prevJobIdsRef.current.delete(jobId);
    setJobs(prev => prev.filter(j => j.id !== jobId));
  };

  const handleCreateClick = () => {
    if (!isPremium && lessons.length >= FREE_LESSON_LIMIT) {
      setShowPremiumModal(true);
    } else {
      setShowCreateModal(true);
    }
  };

  const handleJobCreated = async () => {
    setShowCreateModal(false);
    const newJobs = await loadPendingJobsRaw();
    if (newJobs) {
      prevJobIdsRef.current = new Set(newJobs.map((j: GenerationJob) => j.id));
      setJobs(newJobs);
    }
  };

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'pending': t('Waiting...'),
      'generating_text': t('Generating text...'),
      'analyzing': t('Analyzing content...'),
      'generating_audio': t('Creating audio...'),
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"
            >
              {isRTL ? '→' : '←'}
            </button>
            <div className="flex items-center gap-2">
              <Sparkles className="h-7 w-7 text-amber-600" />
              <h1 className="text-2xl font-bold text-gray-900">{t('Custom Lessons')}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isPremium && (
              <span className="text-sm text-gray-500">
                {lessons.length}/{FREE_LESSON_LIMIT} {t('free lessons')}
              </span>
            )}
            {isPremium && (
              <span className="flex items-center gap-1 text-sm text-amber-600 font-medium">
                <Crown className="h-4 w-4" />
                {t('Premium')}
              </span>
            )}
            <button
              onClick={handleCreateClick}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              {t('Create Lesson')}
            </button>
          </div>
        </div>

        {jobs.length > 0 && (
          <div className="mb-6 space-y-3">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              {t('In Progress')}
            </h2>
            {jobs.map(job => (
              <div key={job.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                    <span className="font-medium text-gray-900">
                      {job.generated_title || (job.input_type === 'ai_generated' ? t('AI-Generated Lesson') : t('Custom Lesson'))}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-amber-600">{job.progress}%</span>
                    <button
                      onClick={() => handleCancelJob(job.id)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title={t('Cancel')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${job.progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">{getStatusText(job.status)}</p>
              </div>
            ))}
          </div>
        )}

        {lessons.length === 0 && jobs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-gray-900 mb-2">{t('No custom lessons yet')}</h2>
            <p className="text-gray-600 mb-6">{t('Create your first AI-powered lesson')}</p>
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              <Sparkles className="h-5 w-5" />
              {t('Create Your First Lesson')}
            </button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {lessons.map(lesson => (
              <div
                key={lesson.id}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{lesson.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2">{lesson.description}</p>
                    </div>
                    {lesson.completed && (
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 ml-2" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <span className="px-2 py-1 bg-gray-100 rounded">{lesson.category}</span>
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">{lesson.difficulty}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(lesson.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onSelectLesson(lesson.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                    >
                      <Play className="h-4 w-4" />
                      {lesson.completed ? t('Review') : t('Start')}
                    </button>
                    <button
                      onClick={() => handleDeleteLesson(lesson.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateLessonModal
          userId={userId}
          nativeLanguage={nativeLanguage}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleJobCreated}
        />
      )}

      {showPremiumModal && (
        <PremiumUpsellModal
          nativeLanguage={nativeLanguage}
          onClose={() => setShowPremiumModal(false)}
        />
      )}
    </div>
  );
}

interface PremiumUpsellModalProps {
  nativeLanguage?: string;
  onClose: () => void;
}

function PremiumUpsellModal({ nativeLanguage, onClose }: PremiumUpsellModalProps) {
  const { t, isRTL } = useTranslation(nativeLanguage);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-8 text-white text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
            <Crown className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{t('Upgrade to Premium')}</h2>
          <p className="text-amber-100 text-sm">
            {t("You've reached the free plan limit of")} {FREE_LESSON_LIMIT} {t('AI lessons')}
          </p>
        </div>

        <div className="p-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <Lock className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <span className="font-semibold text-gray-900">{t('Free Plan Limit Reached')}</span>
            </div>
            <p className="text-sm text-gray-600">
              {t('Free users can create up to')} {FREE_LESSON_LIMIT} {t('AI-powered lessons. Upgrade to Premium for unlimited lesson creation.')}
            </p>
          </div>

          <ul className="space-y-2 mb-6">
            {[
              t('Unlimited AI lesson creation'),
              t('All lesson types and languages'),
              t('Priority lesson generation'),
              t('Full word bank access'),
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                <div className="h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <svg className="h-3 w-3 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                {feature}
              </li>
            ))}
          </ul>

          <p className="text-center text-sm text-gray-500 mb-4">
            {t('Contact an admin to upgrade your account to Premium.')}
          </p>

          <button
            onClick={onClose}
            className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
          >
            {t('Maybe later')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface CreateLessonModalProps {
  userId: string;
  nativeLanguage?: string;
  onClose: () => void;
  onCreated: () => void;
}

const DIFFICULTY_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'] as const;

function CreateLessonModal({ userId, nativeLanguage, onClose, onCreated }: CreateLessonModalProps) {
  const [mode, setMode] = useState<'choose' | 'full_text' | 'ai_generated'>('choose');
  const [inputText, setInputText] = useState('');
  const [wordCount, setWordCount] = useState(150);
  const [translationLanguage, setTranslationLanguage] = useState(nativeLanguage || 'English');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [requestedLevel, setRequestedLevel] = useState<string>('B1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { t, isRTL } = useTranslation(nativeLanguage);

  const handleSubmit = async () => {
    if (!inputText.trim()) {
      setError(t('Please enter text or a topic'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const { data: job, error: insertError } = await supabase
        .from('user_lesson_generation_jobs')
        .insert({
          user_id: userId,
          input_type: mode,
          input_text: inputText.trim(),
          word_count: mode === 'ai_generated' ? wordCount : null,
          translation_language: translationLanguage,
          requested_level: mode === 'ai_generated' ? requestedLevel : null,
          status: 'pending',
          progress: 0,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const { data: session } = await supabase.auth.getSession();
      fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-custom-lesson`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.session?.access_token}`,
          },
          body: JSON.stringify({ jobId: job.id }),
        }
      );

      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Something went wrong'));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-amber-600" />
              <h2 className="text-xl font-semibold text-gray-900">{t('Create Custom Lesson')}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {mode === 'choose' && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-6">{t('How would you like to create your lesson?')}</p>

              <button
                onClick={() => setMode('full_text')}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('I have Dutch text')}</h3>
                    <p className="text-sm text-gray-600">{t('Paste your own Dutch text to create a lesson')}</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setMode('ai_generated')}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Sparkles className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('Generate with AI')}</h3>
                    <p className="text-sm text-gray-600">{t('Describe a topic and AI will create Dutch text')}</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {mode === 'full_text' && (
            <div className="space-y-4">
              <button
                onClick={() => setMode('choose')}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                {isRTL ? '→' : '←'} {t('Back')}
              </button>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Dutch Text')}
                </label>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  rows={8}
                  placeholder={t('Paste your Dutch text here...')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {inputText.split(/\s+/).filter(w => w).length} {t('words')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    {t('Translation Language')}
                  </div>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  >
                    <span>{translationLanguage}</span>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showLanguageDropdown && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {TRANSLATION_LANGUAGES.map(lang => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => {
                            setTranslationLanguage(lang);
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-amber-50 ${translationLanguage === lang ? 'bg-amber-100 text-amber-700' : ''}`}
                        >
                          {lang}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('Sentences will be translated to this language')}
                </p>
              </div>
            </div>
          )}

          {mode === 'ai_generated' && (
            <div className="space-y-4">
              <button
                onClick={() => setMode('choose')}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                {isRTL ? '→' : '←'} {t('Back')}
              </button>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Topic or Description')}
                </label>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  rows={4}
                  placeholder={t('Describe what you want to learn about... (in any language)')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Approximate Word Count')}
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="50"
                    max="500"
                    step="25"
                    value={wordCount}
                    onChange={(e) => setWordCount(Number(e.target.value))}
                    className="flex-1 accent-amber-600"
                  />
                  <span className="text-sm font-medium text-gray-900 w-16 text-right">
                    {wordCount} {t('words')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {wordCount < 100 ? t('Short') : wordCount < 250 ? t('Medium') : t('Long')} {t('lesson')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Difficulty Level')}
                </label>
                <div className="flex gap-2">
                  {DIFFICULTY_LEVELS.map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setRequestedLevel(level)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        requestedLevel === level
                          ? 'bg-amber-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {requestedLevel === 'A1' && t('Beginner - basic vocabulary and simple sentences')}
                  {requestedLevel === 'A2' && t('Elementary - everyday expressions and simple phrases')}
                  {requestedLevel === 'B1' && t('Intermediate - familiar topics and standard language')}
                  {requestedLevel === 'B2' && t('Upper-intermediate - complex texts and abstract topics')}
                  {requestedLevel === 'C1' && t('Advanced - demanding texts and sophisticated grammar')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    {t('Translation Language')}
                  </div>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  >
                    <span>{translationLanguage}</span>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showLanguageDropdown && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {TRANSLATION_LANGUAGES.map(lang => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => {
                            setTranslationLanguage(lang);
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-amber-50 ${translationLanguage === lang ? 'bg-amber-100 text-amber-700' : ''}`}
                        >
                          {lang}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('Sentences will be translated to this language')}
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}
        </div>

        {mode !== 'choose' && (
          <div className="p-6 border-t bg-gray-50">
            <button
              onClick={handleSubmit}
              disabled={submitting || !inputText.trim()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:bg-amber-400 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('Creating...')}
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5" />
                  {t('Create Lesson')}
                </>
              )}
            </button>
            <p className="text-xs text-gray-500 text-center mt-3">
              {t('This may take a few minutes')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
